<template>
    <div :class="className" :style="styleCss">
        <slot
            name="trigger"
            :click="openModal"
            :close="closeModal"
            :closeModal="closeModal"
        >
            <button
                type="button"
                class="wf-btn-primary"
                v-html="buttonLabel"
                @click="openModal"
            ></button>
        </slot>

        <!-- Modal Overlay -->
        <Teleport to="body">
            <transition
                enter-active-class="transition ease-out duration-300"
                enter-from-class="opacity-0"
                enter-to-class="opacity-100"
                leave-active-class="transition ease-in duration-200"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
            >
                <div
                    v-if="isOpen"
                    class="fixed inset-0 z-[100] overflow-y-auto opacity-100"
                    aria-labelledby="modal-title"
                    role="dialog"
                    aria-modal="true"
                >
                    <div
                        class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
                    >
                        <!-- Backdrop -->
                        <div
                            class="fixed inset-0 bg-black/20 backdrop-blur-sm transition-opacity"
                            aria-hidden="true"
                            @click="onBackdropClick"
                        ></div>

                        <!-- This element is to trick the browser into centering the modal contents. -->
                        <span
                            class="hidden sm:inline-block sm:align-middle sm:h-screen"
                            aria-hidden="true"
                            >&#8203;</span
                        >

                        <!-- Modal Card -->
                        <transition
                            enter-active-class="transition ease-out duration-300"
                            enter-from-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enter-to-class="opacity-100 translate-y-0 sm:scale-100"
                            leave-active-class="transition ease-in duration-200"
                            leave-from-class="opacity-100 translate-y-0 sm:scale-100"
                            leave-to-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <div
                                v-if="isOpen"
                                class="inline-block align-bottom bg-white rounded-[2rem] text-left border border-black/5 transform transition-all sm:my-8 sm:align-middle w-full mx-auto overflow-hidden shadow-modal"
                                :class="[
                                    resolvedModalClassName,
                                    modalBodyClassName,
                                ]"
                            >
                                <!-- Close Button (Absolute) -->
                                <button
                                    v-if="showClose && !disableClose"
                                    @click="closeModal"
                                    class="absolute top-5 right-5 w-10 h-10 flex items-center justify-center rounded-full text-black/45 hover:text-black/70 hover:bg-black/5 transition-all z-10"
                                >
                                    <svg
                                        class="w-6 h-6"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            stroke-width="2"
                                            d="M6 18L18 6M6 6l12 12"
                                        />
                                    </svg>
                                </button>

                                <div
                                    class="card card-modal border-0 shadow-none"
                                >
                                    <!-- Header -->
                                    <div
                                        v-if="showHeader"
                                        class="px-8 pt-8 pb-5"
                                    >
                                        <slot
                                            name="header"
                                            :close="closeModal"
                                            :closeModal="closeModal"
                                        >
                                            <slot
                                                name="header-title"
                                                :close="closeModal"
                                                :closeModal="closeModal"
                                            >
                                                <h3
                                                    class="text-xl font-semibold text-gray-900"
                                                    id="modal-title"
                                                    v-html="headerTitle"
                                                ></h3>
                                            </slot>
                                        </slot>
                                    </div>

                                    <!-- Body -->
                                    <div v-if="showBody" class="px-8 pb-6">
                                        <slot
                                            name="body"
                                            :close="closeModal"
                                            :closeModal="closeModal"
                                        ></slot>
                                    </div>

                                    <div
                                        v-if="showFooter"
                                        class="px-8 py-5 bg-black/[0.015] border-t border-black/10 flex items-center justify-end gap-3"
                                    >
                                        <slot
                                            name="footer-close"
                                            :close="closeModal"
                                            :closeModal="closeModal"
                                        >
                                            <button
                                                type="button"
                                                class="wf-btn-light"
                                                @click="closeModal"
                                            >
                                                {{ closeLabel }}
                                            </button>
                                        </slot>
                                        <slot
                                            name="footer-button"
                                            :close="closeModal"
                                            :closeModal="closeModal"
                                        >
                                            <button
                                                type="button"
                                                class="wf-btn-primary"
                                            >
                                                Save changes
                                            </button>
                                        </slot>
                                    </div>
                                </div>
                            </div>
                        </transition>
                    </div>
                </div>
            </transition>
        </Teleport>
    </div>
</template>

<script>
export default {
    name: "ModalWf",
    props: {
        styleCss: {
            type: String,
            default: "",
        },
        className: {
            type: String,
            default: "",
        },
        modalClassName: {
            type: String,
            default: "max-w-2xl",
        },
        modalBodyClassName: {
            type: String,
            default: "",
        },
        autoOpen: {
            type: [Boolean, Number, String],
            default: false,
        },
        disableClose: {
            type: [Boolean, Number, String],
            default: false,
        },
        defaultBackdrop: {
            type: [String, Boolean],
            default: true,
        },
        showHeader: {
            type: [Boolean, Number, String],
            default: true,
        },
        showBody: {
            type: [Boolean, Number, String],
            default: true,
        },
        showFooter: {
            type: [Boolean, Number, String],
            default: false,
        },
        showClose: {
            type: [Boolean, Number, String],
            default: true,
        },
        headerTitle: {
            type: String,
            default: "",
        },
        buttonLabel: {
            type: String,
            default: "",
        },
        closeLabel: {
            type: String,
            default: "Close",
        },
    },
    data() {
        return {
            isOpen: false,
        };
    },
    computed: {
        resolvedModalClassName() {
            // Map legacy Bootstrap classes to Tailwind
            const mapping = {
                "modal-sm": "max-w-md",
                "modal-md": "max-w-2xl",
                "modal-lg": "max-w-4xl",
                "modal-xl": "max-w-6xl",
                "modal-full": "max-w-[95%]",
            };

            return (
                mapping[this.modalClassName] ||
                this.modalClassName ||
                "max-w-2xl"
            );
        },
    },
    mounted() {
        if (!!+this.autoOpen) {
            this.openModal();
        }
    },
    methods: {
        openModal(evt) {
            if (evt && evt.preventDefault) evt.preventDefault();
            this.isOpen = true;
            document.body.classList.add("overflow-hidden");
        },
        closeModal() {
            if (!!+this.disableClose) return;
            this.isOpen = false;
            document.body.classList.remove("overflow-hidden");
        },
        onBackdropClick() {
            if (!this.disableClose && this.defaultBackdrop) {
                this.closeModal();
            }
        },
    },
    beforeUnmount() {
        document.body.classList.remove("overflow-hidden");
    },
};
</script>
